import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, PlusCircle, Briefcase, Users, Building2, LogOut, Menu,
  CheckCircle2, Clock, XCircle, MapPin, IndianRupee, Trash2, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "post", label: "Post a Job", icon: PlusCircle },
  { key: "jobs", label: "My Jobs", icon: Briefcase },
  { key: "applicants", label: "Applicants", icon: Users },
  { key: "profile", label: "Company Profile", icon: Building2 },
];

const JOB_STATUS = {
  pending: { label: "Pending Approval", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  published: { label: "Live", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-700 border-rose-200", icon: XCircle },
  closed: { label: "Closed", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: XCircle },
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock },
};
const APP_STATUS = {
  "Applied": "bg-slate-100 text-slate-700 border-slate-200",
  "Under Review": "bg-amber-50 text-amber-700 border-amber-200",
  "Shortlisted": "bg-blue-50 text-blue-700 border-blue-200",
  "Interview Scheduled": "bg-violet-50 text-violet-700 border-violet-200",
  "Selected": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Joined": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Rejected": "bg-rose-50 text-rose-700 border-rose-200",
};

const EMPTY_JOB = {
  title: "", location: "", industry: "", category: "", employment_type: "Full-time",
  salary_min: "", salary_max: "", experience: "", qualification: "", vacancies: "",
  working_hours: "", deadline: "", skills: "", description: "", responsibilities: "",
  requirements: "", benefits: "",
};

export default function EmployerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({});
  const [jobs, setJobs] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [profile, setProfile] = useState({});
  const [pform, setPform] = useState({});
  const [jform, setJform] = useState(EMPTY_JOB);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.documentElement.classList.add("theme-light"); return () => document.documentElement.classList.remove("theme-light"); }, []);

  const load = useCallback(async () => {
    try {
      const [s, j, a, p] = await Promise.all([
        api.get("/employer/stats"), api.get("/employer/jobs"),
        api.get("/employer/applications"), api.get("/employer/me"),
      ]);
      setStats(s.data); setJobs(j.data); setApplicants(a.data); setProfile(p.data);
      setPform({
        company: p.data.company || "", contact_person: p.data.contact_person || "", phone: p.data.phone || "",
        website: p.data.website || "", industry: p.data.industry || "", location: p.data.location || "",
        company_size: p.data.company_size || "", about: p.data.about || "",
      });
    } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const jupd = (k) => (e) => setJform((f) => ({ ...f, [k]: e.target.value }));
  const pupd = (k) => (e) => setPform((f) => ({ ...f, [k]: e.target.value }));

  const toList = (s) => String(s || "").split("\n").map((x) => x.trim()).filter(Boolean);
  const toTags = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

  const submitJob = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...jform,
        salary_min: jform.salary_min ? Number(jform.salary_min) : null,
        salary_max: jform.salary_max ? Number(jform.salary_max) : null,
        vacancies: jform.vacancies ? Number(jform.vacancies) : null,
        skills: toTags(jform.skills),
        responsibilities: toList(jform.responsibilities),
        requirements: toList(jform.requirements),
        benefits: toList(jform.benefits),
      };
      if (editId) { await api.put(`/employer/jobs/${editId}`, payload); toast.success("Job updated — sent for re-approval"); }
      else { await api.post("/employer/jobs", payload); toast.success("Job submitted for admin approval"); }
      setJform(EMPTY_JOB); setEditId(null); setTab("jobs"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit job"); }
    finally { setBusy(false); }
  };

  const editJob = (j) => {
    setJform({
      title: j.title || "", location: j.location || "", industry: j.industry || "", category: j.category || "",
      employment_type: j.employment_type || "Full-time", salary_min: j.salary_min || "", salary_max: j.salary_max || "",
      experience: j.experience || "", qualification: j.qualification || "", vacancies: j.vacancies || "",
      working_hours: j.working_hours || "", deadline: j.deadline || "", skills: (j.skills || []).join(", "),
      description: j.description || "", responsibilities: (j.responsibilities || []).join("\n"),
      requirements: (j.requirements || []).join("\n"), benefits: (j.benefits || []).join("\n"),
    });
    setEditId(j.id); setTab("post");
  };
  const delJob = async (j) => {
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/employer/jobs/${j.id}`); toast.success("Job deleted"); load();
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.put("/employer/me", pform); setProfile(data); toast.success("Company profile saved"); }
    catch (err) { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const statCards = [
    { label: "Total Jobs", value: stats.total_jobs || 0, icon: Briefcase, tab: "jobs" },
    { label: "Live Jobs", value: stats.published || 0, icon: CheckCircle2, tab: "jobs" },
    { label: "Pending", value: stats.pending || 0, icon: Clock, tab: "jobs" },
    { label: "Applicants", value: stats.applications || 0, icon: Users, tab: "applicants" },
  ];

  return (
    <div className="min-h-screen bg-[#F5F7FA] text-slate-800 flex">
      <Seo title="Employer Dashboard" noindex />
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <Link to="/" className="flex items-center h-16 px-5 border-b border-slate-200"><img src="/logo.png" alt="Dreamzz HR" className="h-9 w-auto" /></Link>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {TABS.map((t) => {
            const I = t.icon; const active = tab === t.key;
            return (
              <button key={t.key} data-testid={`emp-tab-${t.key}`} onClick={() => { setTab(t.key); setSidebarOpen(false); if (t.key === "post") { setJform(EMPTY_JOB); setEditId(null); } }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-brand-emerald text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <I size={18} />{t.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <button data-testid="emp-logout" onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50"><LogOut size={18} />Logout</button>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 h-16 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setSidebarOpen(true)} aria-label="Open menu"><Menu size={22} /></button>
          <div>
            <h1 className="font-display font-bold text-lg leading-none">{TABS.find((t) => t.key === tab)?.label}</h1>
            <p className="text-xs text-slate-500 mt-0.5">{profile.company || user?.name}</p>
          </div>
          <Button onClick={() => { setJform(EMPTY_JOB); setEditId(null); setTab("post"); }} className="ml-auto bg-brand-emerald hover:bg-[#C62828] text-white h-9"><PlusCircle size={16} className="mr-1.5" />Post Job</Button>
        </header>

        <main className="flex-1 p-4 sm:p-6 max-w-5xl w-full">
          {tab === "overview" && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map((s) => { const I = s.icon; return (
                  <button key={s.label} onClick={() => setTab(s.tab)} className="text-left p-4 rounded-2xl bg-white border border-slate-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand-emerald/10 text-brand-emerald"><I size={20} /></span>
                      <span className="font-display font-extrabold text-2xl">{s.value}</span>
                    </div>
                    <p className="mt-3 text-sm text-slate-500">{s.label}</p>
                  </button>
                ); })}
              </div>
              <div className="p-5 rounded-2xl bg-white border border-slate-200">
                <div className="flex items-center gap-2 text-sm text-slate-600"><Clock size={16} className="text-amber-500" />New jobs are reviewed by our team before going live. You'll see the status update here.</div>
              </div>
              <div className="p-5 rounded-2xl bg-white border border-slate-200">
                <h2 className="font-semibold mb-4">Recent Applicants</h2>
                {applicants.length === 0 ? <p className="text-sm text-slate-500">No applicants yet.</p> : (
                  <div className="space-y-3">{applicants.slice(0, 5).map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-4 p-3 rounded-xl bg-slate-50">
                      <div className="min-w-0"><p className="font-medium truncate">{a.name}</p><p className="text-xs text-slate-500">{a.job_title}</p></div>
                      <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${APP_STATUS[a.status] || APP_STATUS.Applied}`}>{a.status || "Applied"}</span>
                    </div>
                  ))}</div>
                )}
              </div>
            </div>
          )}

          {tab === "post" && (
            <form onSubmit={submitJob} className="p-5 sm:p-6 rounded-2xl bg-white border border-slate-200 grid sm:grid-cols-2 gap-4">
              {editId && <div className="sm:col-span-2 text-sm bg-amber-50 text-amber-700 border border-amber-200 rounded-lg px-3 py-2">Editing a job resets it to <strong>Pending Approval</strong>.</div>}
              <JFld label="Job Title *" k="title" v={jform} on={jupd} req />
              <JFld label="Location *" k="location" v={jform} on={jupd} req />
              <JFld label="Industry" k="industry" v={jform} on={jupd} />
              <div>
                <Label>Employment Type</Label>
                <Select value={jform.employment_type} onValueChange={(v) => setJform((f) => ({ ...f, employment_type: v }))}>
                  <SelectTrigger data-testid="emp-job-type" className="mt-1 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Full-time", "Part-time", "Contract", "Temporary", "Internship"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <JFld label="Salary Min (₹/month)" k="salary_min" v={jform} on={jupd} type="number" />
              <JFld label="Salary Max (₹/month)" k="salary_max" v={jform} on={jupd} type="number" />
              <JFld label="Experience" k="experience" v={jform} on={jupd} placeholder="e.g. 2-4 years" />
              <JFld label="Qualification" k="qualification" v={jform} on={jupd} />
              <JFld label="Vacancies" k="vacancies" v={jform} on={jupd} type="number" />
              <JFld label="Working Hours" k="working_hours" v={jform} on={jupd} />
              <JFld label="Application Deadline" k="deadline" v={jform} on={jupd} placeholder="e.g. 30 Jun 2026" />
              <div className="sm:col-span-2"><Label>Skills (comma separated)</Label><Input data-testid="emp-job-skills" value={jform.skills} onChange={jupd("skills")} className="mt-1 border-slate-200" /></div>
              <div className="sm:col-span-2"><Label>Job Description</Label><Textarea data-testid="emp-job-desc" value={jform.description} onChange={jupd("description")} rows={4} className="mt-1 border-slate-200" /></div>
              <div className="sm:col-span-2"><Label>Responsibilities (one per line)</Label><Textarea value={jform.responsibilities} onChange={jupd("responsibilities")} rows={3} className="mt-1 border-slate-200" /></div>
              <div className="sm:col-span-2"><Label>Requirements (one per line)</Label><Textarea value={jform.requirements} onChange={jupd("requirements")} rows={3} className="mt-1 border-slate-200" /></div>
              <div className="sm:col-span-2"><Label>Benefits (one per line)</Label><Textarea value={jform.benefits} onChange={jupd("benefits")} rows={2} className="mt-1 border-slate-200" /></div>
              <div className="sm:col-span-2 flex justify-end">
                <Button data-testid="emp-job-submit" type="submit" disabled={busy} className="bg-brand-emerald hover:bg-[#C62828] text-white h-11 px-6">{busy ? "Submitting..." : editId ? "Update Job" : "Submit for Approval"}</Button>
              </div>
            </form>
          )}

          {tab === "jobs" && (
            <div className="space-y-3">
              {jobs.length === 0 ? <Empty icon={Briefcase} title="No jobs yet" body="Post your first job — it will go live after admin approval." onClick={() => setTab("post")} /> :
                jobs.map((j) => { const st = JOB_STATUS[j.status] || JOB_STATUS.pending; const SI = st.icon; return (
                  <div key={j.id} className="p-4 rounded-xl bg-white border border-slate-200 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800">{j.title}</p>
                      <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><MapPin size={13} />{j.location}</span>
                        <span className="flex items-center gap-1"><IndianRupee size={13} />{j.salary || (j.salary_min ? `₹${j.salary_min}+` : "Negotiable")}</span>
                        <span>{j.employment_type}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span className={`text-xs px-2.5 py-1 rounded-full border font-medium inline-flex items-center gap-1 ${st.cls}`}><SI size={12} />{st.label}</span>
                      <div className="flex items-center gap-1">
                        {j.status === "published" && <Link to={`/job/${j.slug}`} className="text-xs text-brand-emerald px-2">View</Link>}
                        <button onClick={() => editJob(j)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="Edit"><Pencil size={15} /></button>
                        <button onClick={() => delJob(j)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500" title="Delete"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </div>
                ); })}
            </div>
          )}

          {tab === "applicants" && (
            <div className="space-y-3">
              {applicants.length === 0 ? <Empty icon={Users} title="No applicants yet" body="Applicants to your live jobs will appear here." /> :
                applicants.map((a) => (
                  <div key={a.id} className="p-4 rounded-xl bg-white border border-slate-200">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-semibold">{a.name}</p>
                        <p className="text-sm text-slate-500">Applied for {a.job_title}</p>
                        <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                          <span>{a.phone}</span><span>{a.email}</span>{a.experience && <span>Exp: {a.experience}</span>}{a.location && <span>{a.location}</span>}
                        </div>
                        {a.cover_note && <p className="mt-2 text-sm text-slate-600 italic">"{a.cover_note}"</p>}
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${APP_STATUS[a.status] || APP_STATUS.Applied}`}>{a.status || "Applied"}</span>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {tab === "profile" && (
            <form onSubmit={saveProfile} className="p-5 sm:p-6 rounded-2xl bg-white border border-slate-200 grid sm:grid-cols-2 gap-4">
              <PFld label="Company Name" k="company" v={pform} on={pupd} />
              <PFld label="Contact Person" k="contact_person" v={pform} on={pupd} />
              <PFld label="Phone" k="phone" v={pform} on={pupd} />
              <PFld label="Website" k="website" v={pform} on={pupd} />
              <PFld label="Industry" k="industry" v={pform} on={pupd} />
              <PFld label="Location" k="location" v={pform} on={pupd} />
              <PFld label="Company Size" k="company_size" v={pform} on={pupd} placeholder="e.g. 11-50" />
              <div className="sm:col-span-2"><Label>About the Company</Label><Textarea value={pform.about} onChange={pupd("about")} rows={3} className="mt-1 border-slate-200" /></div>
              <div className="sm:col-span-2 flex justify-end"><Button data-testid="emp-save-profile" type="submit" disabled={busy} className="bg-brand-emerald hover:bg-[#C62828] text-white h-11 px-6">{busy ? "Saving..." : "Save Profile"}</Button></div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
}

function JFld({ label, k, v, on, type, placeholder, req }) {
  return (<div><Label>{label}</Label><Input data-testid={`emp-job-${k.replace(/_/g, "-")}`} type={type || "text"} required={req} value={v[k]} onChange={on(k)} placeholder={placeholder} className="mt-1 border-slate-200" /></div>);
}
function PFld({ label, k, v, on, placeholder }) {
  return (<div><Label>{label}</Label><Input data-testid={`emp-${k.replace(/_/g, "-")}`} value={v[k] || ""} onChange={on(k)} placeholder={placeholder} className="mt-1 border-slate-200" /></div>);
}
function Empty({ icon: I, title, body, onClick }) {
  return (
    <div className="text-center py-16 rounded-2xl bg-white border border-dashed border-slate-200">
      <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-brand-emerald/10 text-brand-emerald"><I size={26} /></span>
      <h3 className="mt-4 font-display font-bold text-lg">{title}</h3>
      <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">{body}</p>
      {onClick && <Button className="mt-5 bg-brand-emerald hover:bg-[#C62828] text-white" onClick={onClick}>Post a Job</Button>}
    </div>
  );
}
